import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F9]">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[#121215] to-[#2A2A30] text-white p-12 flex-col justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gray-400 font-bold">ERP métier</div>
          <div className="font-display text-4xl font-bold mt-2 tracking-tighter">Pourquoi Pas</div>
          <div className="text-xs text-gray-500 mt-1">Design · Franchi'Mob</div>
        </div>
        <div>
          <div className="font-display text-3xl font-semibold leading-tight tracking-tight">
            Le pilotage<br />intégral<br />de votre activité.
          </div>
          <div className="text-sm text-gray-400 mt-6 max-w-sm">
            Projets, démarches, rentabilité, fournisseurs, contenus.
            Trois clics maximum pour chaque information.
          </div>
          <div className="flex gap-1.5 mt-8">
            <span className="w-8 h-1 bg-[#D05E44] rounded" />
            <span className="w-8 h-1 bg-[#1E3A8A] rounded" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-display text-3xl font-bold tracking-tighter">Connexion</h1>
          <p className="text-sm text-gray-500 mt-1">Accédez à votre espace de pilotage.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 w-full px-3 py-2.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-gray-500">Mot de passe</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-gray-900 hover:bg-black text-white font-semibold text-sm py-2.5 rounded-md transition disabled:opacity-50"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
